import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Building2, TrendingUp, Loader2, Cpu, Sparkles, Wallet } from "lucide-react";
import { toast } from "sonner";

const RISK = { Low: "#10B981", Medium: "#F59E0B", High: "#F97316", Critical: "#EF4444" };

export default function Marketplace() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [budget, setBudget] = useState(100000);
  const [plan, setPlan] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [preferQuantum, setPreferQuantum] = useState(false);

  const load = () => api.get("/marketplace").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const invest = async (l) => {
    if (user === null) return;
    if (!user) { nav("/login"); return; }
    const amt = prompt(`Invest in "${l.title}" (USD):`, "5000");
    if (!amt) return;
    try {
      await api.post(`/marketplace/${l.id}/invest`, { amount: +amt });
      toast.success("Investment escrowed · released on re-inspection");
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const optimize = async () => {
    if (user === null) return;
    if (!user) { nav("/login"); return; }
    setOptimizing(true);
    try {
      const { data } = await api.post("/marketplace/optimize", { budget: +budget, prefer_quantum: preferQuantum });
      setPlan(data);
      toast.success(`Optimized with ${data.backend}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setOptimizing(false); }
  };

  return (
    <div className="min-h-screen bg-[#050A15]">
      <Header />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <p className="text-xs uppercase tracking-widest text-[#8A2BE2]">Escrowed · Re-inspection gated</p>
        <h1 className="font-display font-bold text-3xl mt-1">Retrofit Financing Marketplace</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">High-risk building owners list retrofit needs; investors fund upgrades. Returns are tied to certified safety improvements and rental yield.</p>

        {/* QAOA optimizer */}
        <div className="qs-grad-border p-6 mt-6" data-testid="optimizer">
          <div className="flex items-center gap-2 text-[#8A2BE2]"><Sparkles className="h-5 w-5" /> <span className="text-xs uppercase tracking-widest">QAOA Portfolio Optimizer</span></div>
          <div className="flex flex-wrap items-end gap-4 mt-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Budget (USD)</label>
              <input data-testid="budget-input" type="number" value={budget} onChange={(e) => setBudget(e.target.value)}
                className="bg-[#050A15] border border-white/10 px-3 py-2 font-mono outline-none focus:border-[#8A2BE2]" />
            </div>
            <button data-testid="quantum-opt-toggle" onClick={() => setPreferQuantum(!preferQuantum)}
              className={`flex items-center gap-2 px-3 py-2 border text-sm transition-colors ${preferQuantum ? "border-[#8A2BE2] bg-[#8A2BE2]/10 text-[#8A2BE2]" : "border-white/10 text-slate-400"}`}>
              <Cpu className="h-4 w-4" /> {preferQuantum ? "Quantum" : "Auto"}
            </button>
            <button data-testid="optimize-btn" onClick={optimize} disabled={optimizing || user === null}
              className="qs-cta bg-[#8A2BE2] text-white font-semibold px-5 py-2 flex items-center gap-2 disabled:opacity-60">
              {optimizing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Optimize Allocation"}
            </button>
          </div>
          {plan && (
            <div className="mt-5" data-testid="opt-plan">
              <p className="text-xs font-mono text-slate-500 mb-2">Backend: {plan.backend} · allocated ${plan.allocated.toLocaleString()}</p>
              {plan.quantum && plan.quantum_detail && (
                <div className="border border-[#8A2BE2]/40 bg-[#8A2BE2]/5 p-3 mb-3 text-xs font-mono text-slate-400 space-y-1" data-testid="qaoa-detail">
                  <div className="text-[#8A2BE2]">{plan.quantum_detail.algorithm} · {plan.quantum_detail.qubits} qubits · {plan.quantum_detail.shots} shots{plan.quantum_detail.hardware ? " · IBM hardware" : " · Aer simulator"}</div>
                  {plan.quantum_detail.angles && <div>θ = [{plan.quantum_detail.angles.params?.join(", ")}] · ⟨H⟩ {plan.quantum_detail.angles.energy} · {plan.quantum_detail.angles.evaluations} evals</div>}
                  {plan.quantum_detail.top_bitstrings && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {plan.quantum_detail.top_bitstrings.map((b) => (
                        <span key={b.bits} className="px-1.5 py-0.5 bg-[#050A15] border border-white/10">|{b.bits}⟩ <span className="text-slate-500">×{b.count}</span></span>
                      ))}
                    </div>
                  )}
                  {plan.quantum_detail.fallback_reason && <div className="text-[#F59E0B]">Fallback: {plan.quantum_detail.fallback_reason}</div>}
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-2">
                {plan.plan.map((p) => (
                  <div key={p.listing_id} className="border border-white/10 p-3 text-sm flex justify-between">
                    <span>{p.title} <span className="text-slate-500 text-xs">· {p.city}</span></span>
                    <span className="font-mono text-[#00FF7F]">${p.allocate.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-8" data-testid="listings">
          {items === null ? <div className="col-span-3 grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#00FFFF]" /></div>
            : items.map((l) => {
              const pct = Math.min(100, (l.funded_amount / l.funding_goal) * 100);
              return (
                <div key={l.id} className="qs-panel qs-lift p-6 flex flex-col">
                  <div className="flex items-start justify-between">
                    <Building2 className="h-6 w-6 text-slate-400" />
                    <span className="text-[10px] font-mono uppercase px-2 py-1" style={{ background: `${RISK[l.risk_level]}22`, color: RISK[l.risk_level] }}>{l.risk_level}</span>
                  </div>
                  <h3 className="font-display font-semibold text-lg mt-4">{l.title}</h3>
                  <p className="text-xs text-slate-500 font-mono mt-1">{l.city} · {l.stories} stories</p>
                  <p className="text-sm text-slate-400 mt-3 flex-1 leading-relaxed">{l.description}</p>

                  <div className="mt-4">
                    <div className="flex justify-between text-xs font-mono text-slate-400 mb-1">
                      <span>${l.funded_amount.toLocaleString()}</span><span>${l.funding_goal.toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 bg-[#050A15]"><div className="h-full bg-gradient-to-r from-[#8A2BE2] to-[#00FF7F]" style={{ width: `${pct}%` }} /></div>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <span className="flex items-center gap-1 text-sm text-[#00FF7F] font-mono"><TrendingUp className="h-4 w-4" /> {l.expected_yield_pct}% yield</span>
                    {l.status === "funded" ? (
                      <span className="text-xs font-mono text-[#00FF7F] uppercase">Funded</span>
                    ) : (
                      <button data-testid={`invest-${l.id}`} onClick={() => invest(l)}
                        className="qs-cta bg-[#00FFFF] text-[#050A15] font-semibold px-4 py-2 text-sm flex items-center gap-1.5">
                        <Wallet className="h-3.5 w-3.5" /> Invest
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
      <Footer />
    </div>
  );
}
